import { useMemo, useState } from 'react'
import { Trash2, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/i18n'
import { useTaxonomy } from './TaxonomyContext'
import type { BatchTextField } from './bulkField'

/** Classification patch applied to every selected node (ENG-2624). */
export interface BulkClassification {
  pnid_level_i: string
  pnid_level_ii?: string
  pnid_level_iii?: string
}

interface MultiSelectPanelV2Props {
  count: number
  /** Clear the selection (X). */
  onClose: () => void
  /** Apply the chosen classification to every selected object. */
  onApplyClassification: (levels: BulkClassification) => void
  /** Delete every selected object. */
  onDelete: () => void
  /**
   * ENG-2632: set a text field (Tag / Annotation Text / Note) to `value` across
   * every selected object. Optional — the fill/clear section only renders when both
   * this and {@link onClearField} are provided.
   */
  onFillField?: (field: BatchTextField, value: string) => void
  /** ENG-2632: clear a text field across every selected object. */
  onClearField?: (field: BatchTextField) => void
}

/**
 * ENG-2624 — the Node Details panel in its multi-selection state. Instead of a
 * separate floating bar, a multi-selection reuses the same right-side panel and
 * exposes only the fields that make sense across many objects: Category →
 * Subcategory → Type (applied to all) and Remove. Per-node fields (Tag, Note,
 * Annotation Text, Instrument, Confidence) are intentionally single-select only.
 */
export default function MultiSelectPanelV2({ count, onClose, onApplyClassification, onDelete, onFillField, onClearField }: MultiSelectPanelV2Props) {
  const { t } = useTranslation()
  const taxonomy = useTaxonomy()
  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [type, setType] = useState('')
  // ENG-2632: which text field to batch fill/clear, and its pending fill value.
  const [field, setField] = useState<BatchTextField>('tag')
  const [fieldValue, setFieldValue] = useState('')

  const subcategoryOptions = useMemo(() => (category ? taxonomy.getLevelII(category) : []), [category, taxonomy])
  const typeOptions = useMemo(
    () => (category && subcategory ? taxonomy.getLevelIII(category, subcategory) : []),
    [category, subcategory, taxonomy],
  )
  const applyDisabled = taxonomy.isMissingRequiredType(category, subcategory, type)

  const handleApply = () => {
    if (applyDisabled) return
    onApplyClassification({
      pnid_level_i: category,
      pnid_level_ii: subcategory || undefined,
      pnid_level_iii: type || undefined,
    })
  }

  const showFillClear = !!onFillField && !!onClearField
  const fillDisabled = fieldValue.trim() === ''
  const handleFill = () => {
    if (fillDisabled) return
    onFillField?.(field, fieldValue)
    setFieldValue('')
  }

  const selectClass =
    'w-full h-9 px-3 rounded-md border border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card text-sm text-foreground'

  return (
    <div className="w-[391px] h-full border-l border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card flex flex-col shrink-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0">
        <h3 className="text-base font-semibold text-foreground">
          {t('data_catalog:graph_editor.bulk_actions.selected_count', { count })}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('graph_editor:nodePanel.close', { defaultValue: 'Close' })}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Bulk classification fields */}
      <div className="flex-1 overflow-y-auto px-6 pb-4">
        <div className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground">
            {t('graph_editor:multiSelect.hint', { defaultValue: 'Set a classification to apply to every selected object.' })}
          </p>
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.category', { defaultValue: 'Category' })}</label>
            <select
              aria-label={t('graph_editor:nodePanel.category', { defaultValue: 'Category' })}
              value={category}
              onChange={(e) => { setCategory(e.target.value); setSubcategory(''); setType('') }}
              className={selectClass}
            >
              <option value="">{t('graph_editor:nodePanel.selectCategory', { defaultValue: 'Select category' })}</option>
              {taxonomy.levelIValues.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.subcategory', { defaultValue: 'Subcategory' })}</label>
            <select
              aria-label={t('graph_editor:nodePanel.subcategory', { defaultValue: 'Subcategory' })}
              value={subcategory}
              onChange={(e) => { setSubcategory(e.target.value); setType('') }}
              disabled={!category}
              className={selectClass}
            >
              <option value="">{t('graph_editor:nodePanel.selectSubcategory', { defaultValue: 'Select subcategory' })}</option>
              {subcategoryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:nodePanel.type', { defaultValue: 'Type' })}</label>
            <select
              aria-label={t('graph_editor:nodePanel.type', { defaultValue: 'Type' })}
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={!subcategory || typeOptions.length === 0}
              className={selectClass}
            >
              <option value="">{t('graph_editor:nodePanel.selectType', { defaultValue: 'Select type' })}</option>
              {typeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <Button
            size="sm"
            className="gap-1 w-full"
            disabled={applyDisabled}
            onClick={handleApply}
            title={applyDisabled
              ? String(t('graph_editor:bulkActions.applyBlocked', { defaultValue: 'Pick a full classification (Category, Subcategory, Type) to apply' }))
              : undefined}
          >
            <Check className="w-4 h-4" />
            {t('graph_editor:bulkActions.apply', { count, defaultValue: 'Apply to {{count}}' })}
          </Button>

          {/* ENG-2632: batch fill / clear a text field across the whole selection. */}
          {showFillClear && (
            <div className="flex flex-col gap-3 border-t border-[#e5e5e5] dark:border-dark-border pt-5">
              <p className="text-sm text-muted-foreground">
                {t('graph_editor:bulkActions.fillClearHint', { defaultValue: 'Set or clear one field on every selected object.' })}
              </p>
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:bulkActions.field', { defaultValue: 'Field' })}</label>
                <select
                  aria-label={t('graph_editor:bulkActions.field', { defaultValue: 'Field' })}
                  value={field}
                  // Clear the pending value on field switch so a value typed for one
                  // field isn't accidentally filled into another.
                  onChange={(e) => { setField(e.target.value as BatchTextField); setFieldValue('') }}
                  className={selectClass}
                >
                  <option value="tag">{t('graph_editor:nodePanel.label', { defaultValue: 'Tag' })}</option>
                  <option value="annotation_text">{t('graph_editor:nodePanel.annotationText', { defaultValue: 'Annotation Text' })}</option>
                  <option value="note">{t('graph_editor:nodePanel.note', { defaultValue: 'Note' })}</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-foreground mb-1.5 block">{t('graph_editor:bulkActions.value', { defaultValue: 'Value' })}</label>
                <input
                  type="text"
                  aria-label={t('graph_editor:bulkActions.value', { defaultValue: 'Value' })}
                  value={fieldValue}
                  onChange={(e) => setFieldValue(e.target.value)}
                  placeholder={String(t('graph_editor:bulkActions.valuePlaceholder', { defaultValue: 'Enter a value…' }))}
                  className={selectClass}
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="gap-1 flex-1" disabled={fillDisabled} onClick={handleFill}>
                  <Check className="w-4 h-4" />
                  {t('graph_editor:bulkActions.fill', { count, defaultValue: 'Fill {{count}}' })}
                </Button>
                <Button size="sm" variant="outline" className="gap-1 flex-1" onClick={() => onClearField?.(field)}>
                  <X className="w-4 h-4" />
                  {t('graph_editor:bulkActions.clear', { count, defaultValue: 'Clear {{count}}' })}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom actions */}
      <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-[#e5e5e5] dark:border-dark-border shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
          onClick={onDelete}
        >
          <Trash2 className="w-4 h-4" />
          {t('graph_editor:nodePanel.remove', { defaultValue: 'Remove' })}
        </Button>
      </div>
    </div>
  )
}
